import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import { MessageService } from './message.service';

interface ApiError {
  timestamp?: string;
  status: number;
  error: string;
  message: string;
  path?: string;
  errorId?: string;
}

@Injectable({ providedIn: 'root' })
export class ErrorHandlerService {
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);

  private shownErrors = new Map<string, number>();
  private readonly DEDUPE_WINDOW = 2000;

  private shouldShow(key: string): boolean {
    const now = Date.now();
    const last = this.shownErrors.get(key);

    if (last && now - last < this.DEDUPE_WINDOW) {
      return false;
    }

    this.shownErrors.set(key, now);
    return true;
  }

  /**
   * Główna metoda obsługi błędów HTTP
   * @param err - błąd HTTP z backendu
   * @param skipToast - czy pominąć toast (domyślnie false)
   * @param customMessage - opcjonalna custom wiadomość zamiast tej z backendu
   */
  handleError(err: HttpErrorResponse, skipToast = false, customMessage?: string) {
    const apiError = this.parseApiError(err);
    const showToast = !skipToast;

    // 0 - Anulowane żądanie (np. AbortSignal)
    if (err.status === 0 && err.type === 4) {
      console.log('Request cancelled');
      return;
    }

    // 0 - Brak połączenia z serwerem
    if (err.status === 0) {
      if (showToast && this.shouldShow('NETWORK_ERROR')) {
        this.messageService.showError(
          'Błąd sieci',
          'Serwer jest niedostępny. Sprawdź połączenie internetowe.',
        );
      }
      console.error('Brak połączenia z serwerem', err);
      return;
    }

    // 400 Bad Request
    if (err.status === 400) {
      if (showToast) {
        this.messageService.showWarn('Nieprawidłowe dane', customMessage || apiError.message);
      }
      console.warn('Bad Request:', apiError);
      return;
    }

    // 401 Unauthorized - BEZ TOASTU (obsługuje interceptor)
    if (err.status === 401) {
      console.warn('Nieautoryzowany dostęp:', apiError);
      // Nie pokazuj toastu - interceptor przekieruje do loginu
      return;
    }

    // 403 Forbidden
    if (err.status === 403) {
      if (showToast && this.shouldShow('FORBIDDEN')) {
        this.messageService.showError(
          'Brak dostępu',
          customMessage || 'Nie masz uprawnień do wykonania tej operacji.',
        );
      }

      if (this.router.url !== '/404') {
        void this.router.navigateByUrl('/404', { replaceUrl: true });
      }

      console.warn('Brak uprawnień:', apiError);
      return;
    }

    // 404 Not Found
    if (err.status === 404) {
      if (showToast) {
        this.messageService.showWarn(
          'Nie znaleziono',
          customMessage || apiError.message || 'Zasób nie został znaleziony.',
        );
      }
      console.warn('Nie znaleziono:', apiError);
      return;
    }

    // 409 Conflict
    if (err.status === 409) {
      if (showToast) {
        this.messageService.showWarn('Konflikt danych', customMessage || apiError.message);
      }
      console.warn('Konflikt:', apiError);
      return;
    }

    // 413 Payload Too Large
    if (err.status === 413) {
      if (showToast) {
        this.messageService.showError(
          'Plik za duży',
          customMessage || 'Przesłany plik przekracza maksymalny rozmiar.',
        );
      }
      console.warn('Plik za duży:', apiError);
      return;
    }

    // 422 Unprocessable Entity
    if (err.status === 422) {
      if (showToast) {
        this.messageService.showWarn('Błąd walidacji', customMessage || apiError.message);
      }
      console.warn('Walidacja biznesowa:', apiError);
      return;
    }

    // 500+ Server Error
    if (err.status >= 500) {
      if (showToast && this.shouldShow('SERVER_ERROR')) {
        const errorMessage = apiError.errorId
          ? `Wystąpił nieoczekiwany błąd serwera.\n\nKod błędu: ${apiError.errorId}\n\nSkontaktuj się z supportem, podając powyższy kod.`
          : 'Wystąpił błąd serwera. Spróbuj ponownie później.';

        this.messageService.showError('Błąd serwera', errorMessage);
      }
      console.error('Błąd serwera:', {
        status: err.status,
        errorId: apiError.errorId,
        message: apiError.message,
        fullError: apiError,
      });
      return;
    }

    // Nieobsłużone błędy
    if (showToast) {
      this.messageService.showError(
        'Wystąpił błąd',
        customMessage || apiError.message || 'Nieoczekiwany błąd.',
      );
    }
    console.error('Nieobsłużony błąd HTTP:', err.status, apiError);
  }

  /**
   * Parsuje odpowiedź błędu z backendu do struktury ApiError
   */
  private parseApiError(err: HttpErrorResponse): ApiError {
    // Jeśli backend zwrócił strukturę ApiError
    if (err.error && typeof err.error === 'object') {
      return {
        status: err.error.status || err.status,
        error: err.error.error || 'UNKNOWN_ERROR',
        message: err.error.message || err.message || 'Wystąpił błąd',
        path: err.error.path,
        errorId: err.error.errorId,
        timestamp: err.error.timestamp,
      };
    }

    // Fallback dla niestandardowych odpowiedzi
    return {
      status: err.status,
      error: err.statusText || 'UNKNOWN_ERROR',
      message: typeof err.error === 'string' ? err.error : err.message || 'Wystąpił błąd',
    };
  }

  /**
   * Pomocnicza metoda do wyświetlania walidacji dla formularzy
   * Używaj w przypadku 400 z wieloma błędami walidacji
   */
  handleValidationErrors(err: HttpErrorResponse): string[] {
    const apiError = this.parseApiError(err);

    // Jeśli message zawiera wiele błędów oddzielonych "; "
    if (apiError.message.includes(';')) {
      return apiError.message.split(';').map((msg) => msg.trim());
    }

    return [apiError.message];
  }

  /**
   * Sprawdza czy błąd jest błędem walidacji
   */
  isValidationError(err: HttpErrorResponse): boolean {
    return (
      err.status === 400 &&
      (err.error?.error === 'VALIDATION_ERROR' || err.error?.message?.includes(':'))
    );
  }

  /**
   * Sprawdza czy błąd wymaga interwencji użytkownika
   * (nie pokazuj toastu dla takich błędów automatycznie)
   */
  requiresUserAction(err: HttpErrorResponse): boolean {
    return err.status === 401 || err.status === 403;
  }
}
